import { useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { apiClient } from '../../../services/api';
import { Plan } from '../../../types/domain';

export function useOrganizationBilling(onSubscriptionChanged: () => Promise<void>) {
  const { currentOrg, refreshOrganizations } = useAuth();
  const { showToast } = useToast();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isMutatingSubscription, setIsMutatingSubscription] = useState(false);
  const [upgradeTargetPlan, setUpgradeTargetPlan] = useState<Plan | null>(null);
  const [downgradeTargetPlan, setDowngradeTargetPlan] = useState<Plan | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const fetchPlans = useCallback(async () => {
    setIsLoadingPlans(true);
    try {
      const res = await apiClient<any>('/plans');
      const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
      setPlans(list);
    } catch (err) {
      console.warn('Failed to load plans:', err);
    } finally {
      setIsLoadingPlans(false);
    }
  }, []);

  const handleUpgrade = async () => {
    if (!currentOrg || !upgradeTargetPlan) return;
    setIsMutatingSubscription(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}/subscription`, {
        method: 'POST',
        body: JSON.stringify({ plan: upgradeTargetPlan.slug }),
      });
      showToast({
        type: 'success',
        title: 'Subscription Upgraded',
        message: `Upgraded to ${upgradeTargetPlan.name}. Monthly submission quotas reset for fresh billing cycle.`,
      });
      setUpgradeTargetPlan(null);
      await onSubscriptionChanged();
      await refreshOrganizations();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Upgrade Failed',
        message: err?.message || 'Failed to upgrade subscription.',
      });
    } finally {
      setIsMutatingSubscription(false);
    }
  };

  const handleDowngrade = async () => {
    if (!currentOrg || !downgradeTargetPlan) return;
    setIsMutatingSubscription(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}/subscription/downgrade`, {
        method: 'POST',
        body: JSON.stringify({ plan: downgradeTargetPlan.slug }),
      });
      showToast({
        type: 'success',
        title: 'Downgrade Scheduled',
        message: `Downgrade to ${downgradeTargetPlan.name} scheduled for end of current billing cycle.`,
      });
      setDowngradeTargetPlan(null);
      await onSubscriptionChanged();
      await refreshOrganizations();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Downgrade Failed',
        message: err?.message || 'Failed to schedule downgrade.',
      });
    } finally {
      setIsMutatingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentOrg) return;
    setIsMutatingSubscription(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}/subscription/cancel`, {
        method: 'POST',
      });
      showToast({
        type: 'success',
        title: 'Subscription Cancellation Scheduled',
        message: 'Subscription cancellation scheduled for end of current billing cycle. Reverting to Free tier.',
      });
      setCancelModalOpen(false);
      await onSubscriptionChanged();
      await refreshOrganizations();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Cancellation Failed',
        message: err?.message || 'Failed to cancel subscription.',
      });
    } finally {
      setIsMutatingSubscription(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!currentOrg) return;
    setIsMutatingSubscription(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}/subscription/resume`, {
        method: 'POST',
      });
      showToast({
        type: 'success',
        title: 'Subscription Resumed',
        message: 'Pending plan change canceled. Your current subscription continues uninterrupted.',
      });
      await onSubscriptionChanged();
      await refreshOrganizations();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Resume Failed',
        message: err?.message || 'Failed to resume subscription.',
      });
    } finally {
      setIsMutatingSubscription(false);
    }
  };

  return {
    plans,
    isLoadingPlans,
    fetchPlans,
    isMutatingSubscription,
    upgradeTargetPlan,
    setUpgradeTargetPlan,
    downgradeTargetPlan,
    setDowngradeTargetPlan,
    cancelModalOpen,
    setCancelModalOpen,
    handleUpgrade,
    handleDowngrade,
    handleCancelSubscription,
    handleResumeSubscription,
  };
}
